import { useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setTab } from '../../features/ui/uiSlice';

import { logout, selectAuthUser } from '../../features/auth/authSlice';
import ConfirmDialog from '../common/ConfirmDialog';
import { ShimmerText } from '../common/Shimmer';
import { CHCOLOR } from '../../constants/channels';
import { usePermissions } from '../../features/auth/permissions';
import { useSummaryQuery, useUsersQuery } from '../../services/api';
import { inrShort } from '../../utils/format';

function NavItem({ icon, dot, label, badge, current, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        gap: '11px',
        p: '9px 12px',
        borderRadius: '9px',
        color: current ? '#fff' : 'var(--side-ink)',
        background: current ? 'var(--side-active)' : 'transparent',
        boxShadow: current ? 'var(--side-active-ring)' : 'none',
        fontWeight: current ? 600 : 500,
        fontSize: 13.5,
        mb: '2px',
        justifyContent: 'flex-start',
        transition: '.12s',
        '&:hover': {
          background: current ? 'var(--side-active)' : 'var(--side-hover)',
          color: '#fff',
        },
      }}
    >
      {dot ? (
        <Box component="span" sx={{ width: 9, height: 9, borderRadius: '50%', flex: 'none', background: dot }} />
      ) : (
        <Box component="span" sx={{ width: 17, textAlign: 'center', flex: 'none', opacity: 0.9 }}>{icon}</Box>
      )}
      <Box component="span" sx={{ flex: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </Box>
      {badge != null && (
        <Box
          component="span"
          sx={{
            fontFamily: 'var(--disp)',
            fontSize: 11,
            fontWeight: 600,
            background: current ? 'var(--prism-accent)' : 'var(--side-badge-bg)',
            color: current ? '#12131a' : 'var(--side-badge-ink)',
            p: '1px 7px',
            borderRadius: '20px',
            letterSpacing: '.03em',
          }}
        >
          {badge}
        </Box>
      )}
    </ButtonBase>
  );
}

function NavSection({ heading, children }) {
  return (
    <Box sx={{ p: '14px 16px 2px' }}>
      <Box
        sx={{
          fontFamily: 'var(--disp)',
          fontSize: 10,
          letterSpacing: '.8px',
          color: 'var(--side-label)',
          textTransform: 'uppercase',
          p: '0 6px 8px',
        }}
      >
        {heading}
      </Box>
      {children}
    </Box>
  );
}

export default function Sidebar() {
  const dispatch = useAppDispatch();
  const { tab, range, sidebarOpen } = useAppSelector((s) => s.ui);
  // shares the Users page cache key, so the badge costs no extra request
  const { data: team } = useUsersQuery({ limit: 1 });
  const user = useAppSelector(selectAuthUser);
  // only the channels this account is scoped to — the API would refuse the rest anyway
  const { visibleChannels, isAdmin } = usePermissions();
  const [confirmOpen, setConfirmOpen] = useState(false);
  // same cache key the dashboard uses, so this costs no extra request
  const { data: summary } = useSummaryQuery({ range });
  const revenueByChannel = new Map((summary?.byChannel || []).map((c) => [c.channel, c.rev]));
  const go = (t) => {
    dispatch(setTab(t));
    window.scrollTo(0, 0);
  };

  return (
    <Box
      component="aside"
      sx={{
        // wider than Trust-Prism's 236px: our channel rows carry a revenue badge as well as a label
        width: 264,
        flex: 'none',
        background: 'var(--side-grad)',
        color: 'var(--side-ink)',
        position: 'sticky',
        top: 0,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        '@media (max-width:920px)': {
          position: 'fixed',
          zIndex: 80,
          left: 0,
          top: 0,
          transform: sidebarOpen ? 'none' : 'translateX(-100%)',
          transition: '.22s',
          boxShadow: '0 0 40px rgba(0,0,0,.4)',
        },
      }}
    >
      <Box sx={{ p: '20px 18px 6px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/brand-logo.png`}
          alt="TrustLab"
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
            background: '#fff',
            borderRadius: '12px',
            p: '12px 14px',
            boxShadow: '0 1px 0 rgba(255,255,255,.06)',
          }}
        />
      </Box>
      <Box
        sx={{
          fontFamily: 'var(--disp)',
          fontSize: 9,
          letterSpacing: '.14em',
          color: 'var(--side-label)',
          textTransform: 'uppercase',
          p: '2px 20px 6px',
        }}
      >
        Revenue Ops · by TDPL
      </Box>

      <Box className="side-scroll" sx={{ overflowY: 'auto', flex: 1, pt: 1 }}>
        <NavSection heading="Revenue · Overview">
          <NavItem icon="▦" label="Dashboard" current={tab === 'overall'} onClick={() => go('overall')} />
        </NavSection>

        <NavSection heading="Revenue · Channels">
          {visibleChannels.map((c) => (
            <NavItem
              key={c.id}
              dot={CHCOLOR[c.id]}
              label={c.name}
              badge={summary ? inrShort(revenueByChannel.get(c.id) || 0) : <ShimmerText chars={5} tone="light" />}
              current={tab === c.id}
              onClick={() => go(c.id)}
            />
          ))}
        </NavSection>

        {/* settings are an Admin concern — the team, commission tiers and the pick-lists */}
        {isAdmin && (
        <NavSection heading="Settings">
          <NavItem icon="☺" label="Users" badge={team ? team.total : <ShimmerText chars={2} tone="light" />} current={tab === 'users'} onClick={() => go('users')} />
          <NavItem icon="％" label="Influencer Shares" current={tab === 'influencers'} onClick={() => go('influencers')} />
          <NavItem icon="▾" label="Dropdowns" current={tab === 'options'} onClick={() => go('options')} />
        </NavSection>
        )}
      </Box>

      <Box sx={{ p: '14px 18px', borderTop: '1px solid rgba(255,255,255,.1)' }}>
        <Box sx={{ fontFamily: 'var(--disp)', fontWeight: 600, color: '#fff', fontSize: 14 }}>
          {user?.name || 'Signed out'}
        </Box>
        <Box
          sx={{ fontFamily: 'var(--disp)', fontSize: 10, color: 'var(--prism-accent)', letterSpacing: '.1em', textTransform: 'uppercase' }}
        >
          {user?.role || '—'}
        </Box>
        <ButtonBase
          onClick={() => setConfirmOpen(true)}
          sx={{
            mt: 1.5,
            width: '100%',
            p: '9px',
            border: '1px solid rgba(255,255,255,.16)',
            background: 'transparent',
            color: 'var(--side-ink)',
            borderRadius: '10px',
            fontFamily: 'var(--disp)',
            fontSize: 11,
            letterSpacing: '.06em',
            '&:hover': { background: 'var(--side-hover)', color: '#fff' },
          }}
        >
          ⤷ Sign Out
        </ButtonBase>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        icon="⤷"
        title="Sign out"
        message={`Sign out of Trust Revenue${user?.name ? ` as ${user.name}` : ''}?`}
        detail="Your leads, accounts and settings stay saved in this browser."
        confirmLabel="Sign out"
        cancelLabel="Stay signed in"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          dispatch(logout());
        }}
      />
    </Box>
  );
}
